import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './styles/Form.css';
import  serviceMOSCodes from './military_job_codes.json';

const FORM_COOKIE_KEY = 'dd214_form_data';

const defaultFormData = {
  currentRank: '',
  timeInService: '',
  activeDuty: '',
  branchOfService: '',
  dutyLocation: '',
  contractEndDate: '',
  mos: '',
  qualifications: '',
  evalTrend: '',
  hasDependents: '',
  clearanceLevel: '',
  bachelorDegrees: '',
  postgradDegrees: '',
  civilianCertifications: '',
  nonMilitaryJobs: '',
  otherSkills: '',
  liquidSavings: '',
  yearsToRetirement: '',
  primaryMotivation: '',
  pursueEducation: '',
  educationInCareerField: 'No',
  vaDisabilityRating: '',
  dependentsWithDisabilities: '',
  qualifiesForGiBill: '',
  ownsHome: '',
  recurringExpenses: '',
  militaryComfortLevel: '',
  willingnessToRelocate: '',
  workStyle: '',
  workType: '',
  familyTimeImportance: '',
  retirementType: ''
};

const getCookieValue = (name) => {
  const cookieValue = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`))
    ?.split('=')[1];

  return cookieValue ? decodeURIComponent(cookieValue) : null;
};

const getSavedFormDataFromCookie = () => {
  try {
    const cookieValue = getCookieValue(FORM_COOKIE_KEY);
    if (!cookieValue) return defaultFormData;

    const parsed = JSON.parse(cookieValue);
    return { ...defaultFormData, ...parsed };
  } catch (error) {
    console.error('Failed to parse saved questionnaire cookie:', error);
    return defaultFormData;
  }
};

const persistFormDataToCookie = (data) => {
  const oneDayInSeconds = 60 * 60 * 24;
  document.cookie = `${FORM_COOKIE_KEY}=${encodeURIComponent(JSON.stringify(data))}; path=/; max-age=${oneDayInSeconds}; SameSite=Lax`;
};

function Form() {
  const navigate = useNavigate();
  const formShellRef = useRef(null);
  const [currentSection, setCurrentSection] = useState(0);
  const [formData, setFormData] = useState(getSavedFormDataFromCookie);
  const [openSelectName, setOpenSelectName] = useState('');

  const sections = ['Military', 'Civilian', 'Other'];
  const progressPercent = Math.round(((currentSection + 1) / sections.length) * 100);

  useEffect(() => {
    persistFormDataToCookie(formData);
  }, [formData]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!formShellRef.current) return;
      if (!formShellRef.current.contains(event.target)) {
        setOpenSelectName('');
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpenSelectName('');
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const sanitizeInput = (input) => {
    if (typeof input !== 'string') return input;
    let sanitized = input.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    sanitized = sanitized.replace(/<[^>]*>/g, '');
    sanitized = sanitized.replace(/[<>"'`]/g, '');
    sanitized = sanitized.replace(/(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|UNION|SCRIPT)\b)/gi, '');
    return sanitized.trim();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateSection = (section) => {
    const errors = [];
    if (section === 0) {
      if (!formData.currentRank) errors.push('Current rank is required');
      if (!formData.branchOfService) errors.push('Branch of service is required');
      if (!formData.activeDuty) errors.push('Active duty status is required');
      if (!formData.timeInService) errors.push('Time in service is required');
      if (!formData.dutyLocation) errors.push('Duty Location is required');
      if (!formData.mos) errors.push('Current job code is required');
      if (!formData.qualifications) errors.push('Military qualifications are required');
      if (!formData.evalTrend) errors.push('Eval Trend is required');
      if (!formData.hasDependents) errors.push('Has dependents question is required');
      if (!formData.clearanceLevel) errors.push('Clearance level is required');
    }
    return errors;
  };

  const handleNext = () => {
    const errors = validateSection(currentSection);
    if (errors.length > 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (currentSection < sections.length - 1) {
      setCurrentSection((prev) => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    if (currentSection > 0) {
      setCurrentSection((prev) => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (currentSection < sections.length - 1) {
      handleNext();
      return;
    }

    const errors = validateSection(currentSection);
    if (errors.length > 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const sanitizedData = {};
    Object.keys(formData).forEach((key) => {
      sanitizedData[key] = typeof formData[key] === 'string' ? sanitizeInput(formData[key]) : formData[key];
    });

    navigate('/results', { state: { formData: sanitizedData, streamFromBackend: true } });
  };

  const renderField = ({ label, name, required = false, children, helpText = '' }) => (
    <div className={`form-field ${required ? 'required-field' : ''}`}>
      <label className="form-label" htmlFor={name}>
        {label} {required && <span className="required">*</span>}
      </label>
      {helpText && <p className="form-help">{helpText}</p>}
      {children}
    </div>
  );

  const renderInput = ({ label, name, type = 'text', required = false, placeholder = '', min, max }) =>
    renderField({
      label,
      name,
      required,
      children: (
        <input
          id={name}
          className="form-input"
          type={type}
          name={name}
          value={formData[name]}
          onChange={handleChange}
          placeholder={placeholder}
          min={min}
          max={max}
        />
      )
    });

  const renderDatePicker = ({ label, name, required = false, placeholder = '' }) =>
    renderField({
      label,
      name,
      required,
      children: (
        <DatePicker
          id={name}
          showYearDropdown
          showMonthDropdown
          scrollableYearDropdown
          yearDropdownItemNumber={50}
          minDate={new Date("December 31, 1975 00:00:00")}
          selected={formData[name] ? new Date(formData[name]) : null}
          onChange={(date) => {
            setFormData((prev) => ({
              ...prev,
              [name]: date ? date.toISOString() : ''
            }));
          }}
          placeholderText={placeholder}
          dateFormat="MMMM d, yyyy"
          className="form-input"
          isClearable
        />
      )
    });

  const renderTextarea = ({ label, name, placeholder = '', rows = 3, required = false }) =>
    renderField({
      label,
      name,
      required,
      children: (
        <textarea
          id={name}
          className="form-input form-textarea"
          name={name}
          value={formData[name]}
          onChange={handleChange}
          rows={rows}
          placeholder={placeholder}
        />
      )
    });

  const renderSelect = ({ label, name, options, required = false }) => {
    const isOpen = openSelectName === name;
    const selectedOption = options.find((option) => option.value === formData[name]);
    const selectedLabel = selectedOption ? selectedOption.label : 'Select...';

    return renderField({
      label,
      name,
      required,
      children: (
        <div className="custom-select" data-open={isOpen ? 'true' : 'false'}>
          <button
            id={name}
            type="button"
            className="form-input custom-select-trigger"
            aria-expanded={isOpen}
            aria-controls={`${name}-listbox`}
            aria-haspopup="listbox"
            onClick={() => setOpenSelectName((prev) => (prev === name ? '' : name))}
          >
            {selectedLabel}
          </button>

          {isOpen && (
            <div className="custom-select-menu" id={`${name}-listbox`} role="listbox" aria-label={label}>
              <button
                type="button"
                className={`custom-select-option ${!formData[name] ? 'selected' : ''}`}
                role="option"
                aria-selected={!formData[name]}
                onClick={() => {
                  setFormData((prev) => ({ ...prev, [name]: '' }));
                  setOpenSelectName('');
                }}
              >
                Select...
              </button>
              {options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`custom-select-option ${formData[name] === option.value ? 'selected' : ''}`}
                  role="option"
                  aria-selected={formData[name] === option.value}
                  onClick={() => {
                    setFormData((prev) => ({ ...prev, [name]: option.value }));
                    setOpenSelectName('');
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )
    });
  };

  const renderRadios = ({ label, name, options, required = false }) =>
    renderField({
      label,
      name,
      required,
      children: (
        <div className="radio-group">
          {options.map((option) => (
            <label key={option} className={`radio-chip ${formData[name] === option ? 'active' : ''}`}>
              <input
                type="radio"
                name={name}
                value={option}
                checked={formData[name] === option}
                onChange={handleChange}
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
      )
    });

  const renderMilitarySection = () => (
    <>
      <p className="section-note">
        Please answer all required <span className="required">*</span> questions.
      </p>
      {renderSelect({
        label: 'Current Rank',
        name: 'currentRank',
        required: true,
        options: [
          ...['E-1', 'E-2', 'E-3', 'E-4', 'E-5', 'E-6', 'E-7', 'E-8', 'E-9'].map((r) => ({ value: r, label: r })),
          ...['W-1', 'W-2', 'W-3', 'W-4', 'W-5'].map((r) => ({ value: r, label: r })),
          ...['O-1', 'O-2', 'O-3', 'O-4', 'O-5', 'O-6', 'O-7', 'O-8', 'O-9', 'O-10'].map((r) => ({ value: r, label: r }))
        ]
      })}
      {renderDatePicker({
        label: 'Service Start Date',
        name: 'timeInService',
        required: true,
        placeholder: 'Select service start date'
      })}
      {renderRadios({ label: 'Active Duty or Reserves', name: 'activeDuty', required: true, options: ['Active Duty', 'Reserves'] })}
      {renderRadios({ label: 'Branch of Service', name: 'branchOfService', required: true, options: ['Army', 'Navy', 'Air Force', 'Marine Corps', 'Coast Guard', 'Space Force'] })}
      {renderInput({ label: 'Current Duty Location', name: 'dutyLocation', required: true, placeholder: 'e.g., San Diego, CA' })}
      {renderDatePicker({
        label: 'Contract End Date / Remaining Service Obligation',
        name: 'contractEndDate',
        required: true,
        placeholder: 'Select contract end date'
      })}
      {renderRadios({ label: 'Retirement Type', name: 'retirementType', required: true, options: ['Blended Retirement', 'High Three'] })}
      {
      renderSelect({
        label: 'Current MOS/Designator/Job Code',
        name: 'mos',
        required: true,
        options: ((formData.branchOfService==='Army')&& (formData.currentRank.includes('E')) && Object.values(serviceMOSCodes)?.[0]?.mos 
        /* eslint-disable no-mixed-operators*/
        || (formData.branchOfService==='Army')&& (formData.currentRank.includes('W')) && Object.values(serviceMOSCodes)?.[1]?.mos
        || (formData.branchOfService==='Army')&& (formData.currentRank.includes('O')) && Object.values(serviceMOSCodes)?.[2]?.mos
        || (formData.branchOfService==='Navy')&& (formData.currentRank.includes('E')) && Object.values(serviceMOSCodes)?.[3]?.mos
        || (formData.branchOfService==='Navy')&& (formData.currentRank.includes('W')) && Object.values(serviceMOSCodes)?.[4]?.mos
        || (formData.branchOfService==='Navy')&& (formData.currentRank.includes('O')) && Object.values(serviceMOSCodes)?.[5]?.mos
        || (formData.branchOfService==='Air Force')&& (formData.currentRank.includes('E')) && Object.values(serviceMOSCodes)?.[6]?.mos
        || (formData.branchOfService==='Air Force')&& (formData.currentRank.includes('O')) && Object.values(serviceMOSCodes)?.[8]?.mos
        || (formData.branchOfService==='Marine Corps')&& (formData.currentRank.includes('E')) && Object.values(serviceMOSCodes)?.[9]?.mos
        || (formData.branchOfService==='Marine Corps')&& (formData.currentRank.includes('W')) && Object.values(serviceMOSCodes)?.[10]?.mos
        || (formData.branchOfService==='Marine Corps')&& (formData.currentRank.includes('O')) && Object.values(serviceMOSCodes)?.[11]?.mos
        || (formData.branchOfService==='Space Force')&& (formData.currentRank.includes('E')) && Object.values(serviceMOSCodes)?.[12]?.mos
        || (formData.branchOfService==='Space Force')&& (formData.currentRank.includes('O')) && Object.values(serviceMOSCodes)?.[14]?.mos
        || (formData.branchOfService==='Coast Guard')&& (formData.currentRank.includes('E')) && Object.values(serviceMOSCodes)?.[15]?.mos
        || (formData.branchOfService==='Coast Guard')&& (formData.currentRank.includes('W')) && Object.values(serviceMOSCodes)?.[16]?.mos
        || (formData.branchOfService==='Coast Guard')&& (formData.currentRank.includes('O')) && Object.values(serviceMOSCodes)?.[17]?.mos
        || []).map((r) => ({
          value: r.code + ":\t" + r.title,
          label: `${r.code} - ${r.title}`
        })

        )
      })}
      {renderTextarea({
        label: 'Qualifications/Certifications Received in Service',
        name: 'qualifications',
        required: true,
        rows: 4,
        placeholder: "List relevant qualifications or write 'None'"
      })}
      
      {renderRadios({ label: 'Trend of Last 3 Evaluation Reports', name: 'evalTrend', required: true, options: ['Below Average', 'Average', 'Above Average'] })}
      {renderRadios({ label: 'Have Dependents', name: 'hasDependents', required: true, options: ['Yes', 'No'] })}
      {renderRadios({ label: 'Clearance Level', name: 'clearanceLevel', required: true, options: ['TS-SCI', 'TS', 'Secret', 'Confidential', 'None'] })}
    </>
  );

  const renderCivilianSection = () => (
    <>
      {renderTextarea({
        label: 'List Bachelor Degrees',
        name: 'bachelorDegrees',
        rows: 3,
        placeholder: 'e.g., Bachelor of Science in Computer Science, University of Maryland, 2018'
      })}
      {renderTextarea({
        label: 'List Post Graduate Degrees',
        name: 'postgradDegrees',
        rows: 3,
        placeholder: 'e.g., MBA, Harvard Business School, 2020'
      })}
      {renderTextarea({
        label: 'List Civilian Certifications',
        name: 'civilianCertifications',
        rows: 3,
        placeholder: 'e.g., PMP, AWS Certified Solutions Architect'
      })}
      {renderTextarea({
        label: 'List Non-Military Jobs/Internships',
        name: 'nonMilitaryJobs',
        rows: 4,
        placeholder: 'Civilian work, internships, or volunteer experience'
      })}
      {renderTextarea({
        label: 'Other Skills/Experiences',
        name: 'otherSkills',
        rows: 4,
        placeholder: 'Foreign languages, businesses owned, special projects'
      })}
    </>
  );

  const renderOtherSection = () => (
    <>
      {renderInput({ label: 'Liquid Savings Amount ($)', name: 'liquidSavings', type: 'number', placeholder: 'e.g., 25000' })}
      {renderInput({
        label: 'How Long Do You Want to Work Before Retirement (years)?',
        name: 'yearsToRetirement',
        type: 'number',
        placeholder: 'e.g., 20'
      })}
      {renderSelect({
        label: 'Primary Motivation',
        name: 'primaryMotivation',
        options: [
          { value: 'maximize earnings', label: 'Maximize Earnings' },
          { value: 'comfortable lifestyle', label: 'Comfortable Lifestyle' },
          { value: 'stability', label: 'Stability' },
          { value: 'career growth', label: 'Career Growth' }
        ]
      })}
      {renderRadios({ label: 'Desire to Pursue Additional Education', name: 'pursueEducation', options: ['Yes', 'No', 'Maybe'] })}
      {formData.pursueEducation !== ('') && formData.pursueEducation !== ('No') && (
        <div className="sub-question-wrap">
          {renderRadios({
            label: 'Do you want to pursue a degree in your military career field?',
            name: 'educationInCareerField',
            options: ['Yes', 'No', 'Maybe']
          })}
        </div>
      )}
      {renderInput({
        label: 'Expected VA Disability Rating (%)',
        name: 'vaDisabilityRating',
        type: 'number',
        min: 0,
        max: 100,
        placeholder: 'e.g., 30'
      })}
      {renderRadios({ label: 'Do You Have Dependents with Disabilities?', name: 'dependentsWithDisabilities', options: ['Yes', 'No'] })}
      {renderRadios({ label: 'Do You Qualify for the GI Bill?', name: 'qualifiesForGiBill', options: ['Yes', 'No'] })}
      {renderRadios({ label: 'Do You Own a Home?', name: 'ownsHome', options: ['Yes', 'No'] })}
      {renderTextarea({
        label: 'Large, Recurring Expenses',
        name: 'recurringExpenses',
        rows: 3,
        placeholder: 'e.g., Car payments, student loans, medical expenses'
      })}
      {renderField({
        label: 'How Comfortable Do You Feel Being in the Military? (0-5)',
        name: 'militaryComfortLevel',
        helpText: "0 = I hate the military and can't wait to leave, 5 = I love the military",
        children: (
          <input
            id="militaryComfortLevel"
            className="form-input"
            type="number"
            name="militaryComfortLevel"
            value={formData.militaryComfortLevel}
            onChange={handleChange}
            min="0"
            max="5"
            placeholder="0-5"
          />
        )
      })}
      {renderRadios({ label: 'Willingness to Relocate', name: 'willingnessToRelocate', options: ['Yes', 'No', 'Maybe'] })}
      {renderSelect({
        label: 'Work Style',
        name: 'workStyle',
        options: [
          { value: 'in-person', label: 'In-Person' },
          { value: 'hybrid', label: 'Hybrid' },
          { value: 'remote', label: 'Remote' }
        ]
      })}
      {renderSelect({
        label: 'Work Type',
        name: 'workType',
        options: [
          { value: 'physical', label: 'Physical/Hands-on' },
          { value: 'desk', label: 'Desk Job' },
          { value: 'mixed', label: 'Mixed' }
        ]
      })}
      {renderField({
        label: 'How Much Does Time Away from Friends/Family Affect Your Decision? (0-5)',
        name: 'familyTimeImportance',
        helpText: '0 = No effect, 5 = Need as much time with family as possible',
        children: (
          <input
            id="familyTimeImportance"
            className="form-input"
            type="number"
            name="familyTimeImportance"
            value={formData.familyTimeImportance}
            onChange={handleChange}
            min="0"
            max="5"
            placeholder="0-5"
          />
        )
      })}
    </>
  );

  const renderCurrentSection = () => {
    if (currentSection === 0) return renderMilitarySection();
    if (currentSection === 1) return renderCivilianSection();
    return renderOtherSection();
  };

  return (
    <div className="form-page">
      <div className="form-shell" ref={formShellRef}>
        <header className="form-header">
          <h1>Military Transition Questionnaire</h1>
          <p>Help us determine your total outlook in civilian vs. military life.</p>
          <div className="header-meta-row" aria-live="polite">
            <span className="section-pill">Section {currentSection + 1} of {sections.length}</span>
            <span className="completion-pill">{progressPercent}% Complete</span>
          </div>
        </header>

        <div className="progress-meter" aria-hidden="true">
          <div className="progress-meter-fill" style={{ width: `${progressPercent}%` }} />
        </div>

        <div className="progress-wrap" aria-label="Questionnaire progress">
          {sections.map((section, index) => (
            <React.Fragment key={section}>
              <div className="progress-node-wrap">
                <div className={`progress-node ${index <= currentSection ? 'complete' : ''} ${index === currentSection ? 'current' : ''}`}>
                  {index < currentSection ? '✓' : index + 1}
                </div>
                <div className={`progress-label ${index <= currentSection ? 'complete' : ''}`}>{section}</div>
              </div>
              {index < sections.length - 1 && <div className={`progress-line ${index < currentSection ? 'complete' : ''}`} />}
            </React.Fragment>
          ))}
        </div>

        <form onSubmit={(e) => e.preventDefault()} noValidate>
          <section className="section-card" key={sections[currentSection]}>
            <h2>{sections[currentSection]} Information</h2>
            <div className="section-content">{renderCurrentSection()}</div>
          </section>

          <footer className="form-actions">
            {currentSection > 0 && (
              <button type="button" className="btn btn-secondary" onClick={handleBack}>
                Back
              </button>
            )}
            {currentSection < sections.length - 1 ? (
              <button type="button" className="btn btn-primary" onClick={handleNext}>
                Continue
              </button>
            ) : (
              <button type="button" className="btn btn-success" onClick={handleSubmit}>
                Finish
              </button>
            )}
          </footer>
        </form>
      </div>
    </div>
  );
}

export default Form;
